import { Location } from '@angular/common';
import { Component, HostBinding, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { combineLatest, from, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { BulkUploadFeatureHelperService } from '../../../global/bulk-upload-feature.helper.service';
import { IColumnSettings } from '../../shared/shared/table/components/custom-columns-settings/column-settings';
import {
    CustomColumnsSettingsComponent
} from '../../shared/shared/table/components/custom-columns-settings/custom-columns-settings.component';
import { IUploadFilesServiceToken } from '../contract-control/modals/bulk-upload-modal/upload-files.service';
import { DocumentFileUploadModalComponent } from './modals/document-file-upload-modal/document-file-upload-modal.component';
import { DocumentUploadFileApiService } from './modals/document-file-upload-modal/document-upload-file.api.service';
import { DocumentsBulkUploadComponent } from './modals/documents-bulk-upload/documents-bulk-upload/documents-bulk-upload.component';
import { DocumentListingFacade } from './store/document-listing.facade';
import { DocumentViewVariantEnum } from './store/document-listing.reducers';
import { IGetSavedListingFiltersResponseDTO } from 'app/main/shared/save-listing-filters/save-filters.dto';
import { AdvanceFilterParamType, IAdvanceFilter } from 'app/main/shared/advanced-filters/components/advance-filter';
import { SavedFiltersComponent } from 'app/main/shared/save-listing-filters/components/saved-filters/saved-filters.component';
import { AddNewComponent } from '../general-modals/add-new/add-new.component';
import { TranslocoService } from '@jsverse/transloco';
import { CreatedVia } from '../general-modals/add-new/add-new.dto';
import { SelectTemplateComponent } from '../general-modals/select-template/select-template.component';
import {IDocumentDto} from "./dto/document-listing.dto";
import { GenerateFromDocumentOrPromptModalComponent } from '../general-modals/generate-from-document-or-prompt/generate-from-document-or-prompt-modal.component';
import { GenerateDocumentModalService } from './generate-document-modal.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { PermissionsService } from 'app/global/permissions/permissions.service';


@Component({
    selector: 'app-documents',
    templateUrl: './documents.component.html',
    styleUrls: ['./documents.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: IUploadFilesServiceToken,
            useExisting: DocumentUploadFileApiService
        }]
})
export class DocumentsComponent implements OnInit, OnDestroy {
    @HostBinding('class') hostClass = 'todo-application overflow-visible';

    filtersShowHide: boolean = false;
    savefiltersShowHide: boolean = false;
    private readonly _destroyed$ = new Subject<void>();
    protected readonly DocumentViewVariantEnum = DocumentViewVariantEnum;

    public activeView$ = this._documentListingFacade.activeView$ // 'folder_view', 'chart_view', 'margin_chat_view'
    public search$: Observable<string> = this._documentListingFacade.search$;
    public orderedColumns$: Observable<IColumnSettings[]> = this._documentListingFacade.orderedColumns$;
    public tableColumns$: Observable<IColumnSettings[]> = this._documentListingFacade.allColumns$;
    public isLoadingDone$: Observable<boolean> = this._documentListingFacade.isLoadingDone$;
    public hasAnyFiltersApplied$: Observable<boolean> = this._documentListingFacade.hasAnyFiltersApplied$;
    public savedFiltersList$: Observable<IGetSavedListingFiltersResponseDTO[]> = this._documentListingFacade.savedFilters$;
    public selectedSavedFilterId$: Observable<number> = this._documentListingFacade.selectedSavedFilterId$;
    public searchPhrase: string = '';
    public $createdVia = CreatedVia;
    public canUploadDocuments$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_upload_documents);
    /**
     * Toggle view behalf on view key
     * @param view
     */
    toggleViews(view: DocumentViewVariantEnum) {
        this._documentListingFacade.changeActiveView(view);
        this.savefiltersShowHide = false;
    }


    filtersShowHideClick() {
        this.filtersShowHide = !this.filtersShowHide;
    }

    savefiltersShowHideClick() {
        this.savefiltersShowHide = !this.savefiltersShowHide;
    }


    showNestedChartViews($event: { view: DocumentViewVariantEnum }) {
        this.toggleViews($event.view);
    }

    toggleSidebar(name: string): void {
        this._coreSidebarService.getSidebarRegistry(name).toggleOpen();
    }

    /**
     * Custom columns model
     */
    customColumnsModalClick() {
        combineLatest({
            allColumns: this.tableColumns$,
            orderedColumns: this.orderedColumns$
        })
            .pipe(take(1))
            .subscribe(({ allColumns, orderedColumns }) => {
                const instance = this.modalService.open(CustomColumnsSettingsComponent, {
                    size: 'lg'
                });
                instance.componentInstance.customReOrderColumns = JSON.parse(JSON.stringify(orderedColumns));
                instance.componentInstance.customColumns = JSON.parse(JSON.stringify(allColumns));
                instance.result.then(result => {
                    if (result) {
                        this._documentListingFacade.updateColumnsSettings(result.column_settings);
                    }
                });
            });
    }

    constructor(
        private _coreSidebarService: CoreSidebarService,
        private _documentListingFacade: DocumentListingFacade,
        private modalService: NgbModal,
        private location: Location,
        private route: ActivatedRoute,
        public bulkUploadFeatureHelperService: BulkUploadFeatureHelperService,
        private transloco: TranslocoService,
        private generateTemplateModalService: GenerateDocumentModalService,
        private _permissionService: PermissionsService,
    ) {

    }


    onRowClicked(document: IDocumentDto) {
        this._documentListingFacade.selectDocument(document.id);
    }
    
    /**
     * Open bulk upload modal
     */
    documentUploadFileModalClick() {
        this.canUploadDocuments$.pipe(take(1)).subscribe(canUpload => {
            if (!canUpload) {
                return;
            }
            const modalRef = this.modalService.open(DocumentFileUploadModalComponent, {
                size: 'xl',
            });
            modalRef.componentInstance.files = [];
        });
    }


    documentBulkUploadFileModalClick() {
        const modalRef = this.modalService.open(DocumentsBulkUploadComponent,
            {
                size: 'xl',
                windowClass: 'bulk-upload-modal'
            });

        modalRef.result.finally(() => {
            this._documentListingFacade.closeBulkUploadModal();
        })
    }


    ngOnInit(): void {
        this._documentListingFacade.fetchColumnSettings();
        this._documentListingFacade.fetchDocumentTypes();
        this._documentListingFacade.fetchSavedFilters();
        const query = history.state?.query ?? this.route?.snapshot?.queryParams?.query ?? '';
        if (query) {
            this._documentListingFacade.onSearchChange(query);
        }
        this.search$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(searchValue => this.searchPhrase = searchValue);

        this.isLoadingDone$.pipe(takeUntil(this._destroyed$)).subscribe({
            next: (loading: boolean) => {
                const document_id = history.state?.document_id ?? this.route?.snapshot?.queryParams?.document_id ?? '';
                if (loading && document_id) {
                    this._documentListingFacade.selectDocument(document_id);
                    this.location.replaceState('/documents', '', null);
                }

                if (this.route?.snapshot?.queryParams?.document_id) {
                    this.removeQueryParams();
                }
            }
        });
    }

    removeQueryParams() {
        const url = this.location.path(false);
        const [path] = url.split('?');
        this.location.replaceState(path);
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
        this._destroyed$.complete();
        this._documentListingFacade.resetState();
    }

    onSearchApply() {
        this._documentListingFacade.onSearchChange(this.searchPhrase)
    }

    openExportModal(format: 'csv' | 'xlsx' = 'csv'): void {
        this._documentListingFacade.exportCSV(format);
    }


    /**
     * create a new blank document
     */
    createNewDocument(createdVia:CreatedVia) {
        const addNew = this.modalService.open(AddNewComponent);
        addNew.componentInstance.title = this.transloco.translate("Add New Document (general) - Add New Document");
        addNew.componentInstance.label = this.transloco.translate("Document name (general) - Document name");
        addNew.componentInstance.createdVia = createdVia;
    }
    
    generateDocument() {
        const modal = this.modalService.open(GenerateFromDocumentOrPromptModalComponent);
        modal.componentInstance.generationService = this.generateTemplateModalService;
        modal.componentInstance.showOptions = true;
        modal.componentInstance.title = this.transloco.translate("Modal title (AI document generation modal) - New Document");
        modal.componentInstance.submitButtonTitle = this.transloco.translate("Submission button title (AI document generation modal) - Generate Document");
    }

    openSelectTemplate() {
        this.modalService.open(SelectTemplateComponent, {
          size: 'lg',
          windowClass: 'select-template-modal',
        });
      }

    onRemoveFilter(filter: IAdvanceFilter<AdvanceFilterParamType>) {
        this._documentListingFacade.onRemoveFilter(filter);
    }


    
    onSelectedSavedFilterChange(filterId: number) {
        this.selectedSavedFilterId$.pipe(take(1)).subscribe((selectedFilterId) => {
            if (selectedFilterId === filterId) {
                this._documentListingFacade.restoreFilter(null);
                return;
            }
            this._documentListingFacade.restoreFilter(filterId);
        });
    }


    manageSavedFilters() {
        const instance = this.modalService.open(SavedFiltersComponent, {
            size: 'lg'
        });

        const subscription = combineLatest({
            filters: this._documentListingFacade.savedFilters$
        })
            .pipe(takeUntil(from(instance.result.finally())))
            .subscribe(({ filters }) => {
                (instance.componentInstance as SavedFiltersComponent).filters = filters;
            });
        instance.result.finally(() => {
            subscription.unsubscribe();
        });

        (instance.componentInstance as SavedFiltersComponent).editFilter.pipe(takeUntil(from(instance.result.finally())))
            .subscribe(x => {
                this._documentListingFacade.editFilter(x);
            });
        (instance.componentInstance as SavedFiltersComponent).deleteFilter.pipe(takeUntil(from(instance.result.finally())))
            .subscribe(x => {
                this._documentListingFacade.deleteFilter(x.id);
            });
    }

}
